import { useMemo, useState, useEffect } from "react";
import { useTranscription } from "../../../video/providers/TranscriptionProvider";
import VideoUISubtitleItem from "./VideoUISubtitleItem";
import { useRosterState } from "amazon-chime-sdk-component-library-react";

const DISPLAY_DURATION = 3000;

export function VideoUISubtitles() {
  const { state } = useTranscription();

  const { roster } = useRosterState();

  const attendeeCount = Object.keys(roster).length;

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (state.isTranscriptionDisabled) {
      return;
    }

    const interval = setInterval(() => {
      setNow(Date.now());
    }, 100);
    return () => clearInterval(interval);
  }, [state.isTranscriptionDisabled]);

  const recentFinalSegments = useMemo(() => {
    if (state.isTranscriptionDisabled) {
      return [];
    }

    return state.segments
      .filter(
        (seg) =>
          !seg.isPartial &&
          now - seg.endTime <= DISPLAY_DURATION &&
          seg.text.trim().length > 0
      )
      .sort((a, b) => b.endTime - a.endTime)
      .slice(0, 3);
  }, [state.segments, now, state.isTranscriptionDisabled]);

  const livePartialSegment =
    state.currentSegment?.isPartial && state.currentSegment.text.length > 0
      ? state.currentSegment
      : null;

  const hasContent =
    state.isTranscribing &&
    (recentFinalSegments.length > 0 || !!livePartialSegment);

  if (state.isTranscriptionDisabled) {
    return null;
  }

  if (attendeeCount <= 1) {
    return null;
  }

  // Show loading indicator when transcription is starting
  if (state.isLoading) {
    return (
      <div className="absolute bottom-24 left-4 z-50 w-[calc(100%-4rem)]">
        <div className="flex flex-col gap-4">
          <div className="bg-black/80 text-white px-4 py-2 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Starting transcription...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error message if there's an error
  if (state.error) {
    return (
      <div className="absolute bottom-24 left-4 z-50 w-[calc(100%-4rem)]">
        <div className="flex flex-col gap-4">
          <div className="bg-red-600/80 text-white px-4 py-2 rounded-lg">
            <span>Error: {state.error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!hasContent) return null;

  return (
    <div className="absolute bottom-24 left-4 z-50 w-[calc(100%-4rem)]">
      <div className="flex flex-col gap-4">
        {recentFinalSegments
          .slice()
          .reverse()
          .map((segment) => (
            <VideoUISubtitleItem
              key={segment.id}
              speaker={segment.speakerName}
              text={segment.text}
            />
          ))}
        {livePartialSegment && (
          <VideoUISubtitleItem
            key={livePartialSegment.id}
            speaker={livePartialSegment.speakerName}
            text={livePartialSegment.text}
          />
        )}
      </div>
    </div>
  );
}

export default VideoUISubtitles;
